import { useEffect, useState } from 'react';

// ─── Module-level shared timer ────────────────────────────────────────────────
// A single tick every PHASE_MS drives all useCycleColors instances so every
// animated element stays in perfect phase regardless of mount order.

const PHASE_MS = 1000;

/** CSS transition timing that matches the cycle phase duration. Use as `transition: \`fill ${CYCLE_COLOR_TRANSITION}\`` etc. */
export const CYCLE_COLOR_TRANSITION = '0.8s ease-in-out';

let _tickCount = 0;
const _subscribers = new Set<(tick: number) => void>();
let _timerId: ReturnType<typeof setInterval> | null = null;

function _tick() {
  _tickCount += 1;
  _subscribers.forEach((cb) => cb(_tickCount));
}

function _subscribe(cb: (tick: number) => void): () => void {
  _subscribers.add(cb);
  cb(_tickCount); // immediately sync the new subscriber to the current phase
  if (_timerId === null) {
    _timerId = setInterval(_tick, PHASE_MS);
  }
  return () => {
    _subscribers.delete(cb);
    if (_subscribers.size === 0 && _timerId !== null) {
      clearInterval(_timerId);
      _timerId = null;
      _tickCount = 0; // reset so the next subscription starts cleanly
    }
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cycles through an arbitrary sequence of colors using the module-level shared
 * timer (500 ms per phase). All active instances stay in perfect phase.
 *
 * @param colors - Ordered color sequence to cycle through.
 * @param active - When false, always returns `colors[0]` without subscribing.
 */
export function useCycleColors(colors: string[], active = true): string {
  const [tick, setTick] = useState(() => _tickCount);

  useEffect(() => {
    if (!active) return;
    return _subscribe(setTick);
  }, [active]);

  return active ? colors[tick % colors.length] : colors[0];
}

/**
 * Convenience wrapper for a two-color cycle (blue → green style).
 * Delegates to `useCycleColors`; see that function for details.
 */
export function useCycleColor(colorA: string, colorB: string, active = true): string {
  return useCycleColors([colorA, colorB], active);
}
